import { ref, uploadBytes, getDownloadURL, deleteObject, UploadMetadata } from 'firebase/storage';
import { storage, appId } from '@/config/firebase';
import { DM_ATTACHMENT_LIMITS, getAttachmentType, type Attachment } from '@/types/dm';

/**
 * Storage Service for DM Attachments
 *
 * Handles file uploads to Firebase Storage for direct messages.
 */

// Storage path for DM attachments
const getAttachmentStoragePath = (conversationId: string, filename: string) =>
  `artifacts/${appId}/conversations/${conversationId}/attachments/${Date.now()}_${filename}`;

// Storage path for community chat attachments
const getCommunityAttachmentStoragePath = (filename: string) =>
  `artifacts/${appId}/public/attachments/${Date.now()}_${filename}`;

// Community chat image limits (stricter - PNG/JPG only)
export const COMMUNITY_IMAGE_LIMITS = {
  maxFileSize: 10 * 1024 * 1024, // 10 MB
  allowedTypes: ['image/jpeg', 'image/png'] as const,
};

/**
 * Validate a file before upload
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  if (file.size > DM_ATTACHMENT_LIMITS.maxFileSize) {
    return {
      valid: false,
      error: `File size exceeds ${DM_ATTACHMENT_LIMITS.maxFileSize / (1024 * 1024)}MB limit`,
    };
  }

  if (!(DM_ATTACHMENT_LIMITS.allowedTypes as readonly string[]).includes(file.type)) {
    return {
      valid: false,
      error:
        'File type not allowed. Allowed types: images (jpg, png, gif, webp), documents (pdf, doc, docx, xls, xlsx)',
    };
  }

  return { valid: true };
}

/**
 * Upload a file to Firebase Storage
 */
export async function uploadAttachment(conversationId: string, file: File): Promise<Attachment> {
  // Validate file
  const validation = validateFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Create storage reference
  const storagePath = getAttachmentStoragePath(conversationId, file.name);
  const storageRef = ref(storage, storagePath);

  // Set metadata
  const metadata: UploadMetadata = {
    contentType: file.type,
    customMetadata: {
      originalName: file.name,
      conversationId,
    },
  };

  // Upload file
  const snapshot = await uploadBytes(storageRef, file, metadata);

  // Get download URL
  const url = await getDownloadURL(snapshot.ref);

  // Return attachment object
  return {
    type: getAttachmentType(file.type),
    url,
    name: file.name,
    size: file.size,
    mimeType: file.type,
    storagePath,
  };
}

/**
 * Upload multiple files
 */
export async function uploadAttachments(
  conversationId: string,
  files: File[]
): Promise<Attachment[]> {
  const uploads = files.map((file) => uploadAttachment(conversationId, file));
  return Promise.all(uploads);
}

/**
 * Delete an attachment from Firebase Storage
 */
export async function deleteAttachment(storagePath: string): Promise<void> {
  const storageRef = ref(storage, storagePath);
  await deleteObject(storageRef);
}

/**
 * Delete multiple attachments
 */
export async function deleteAttachments(storagePaths: string[]): Promise<void> {
  const deletions = storagePaths.map((path) => deleteAttachment(path));
  await Promise.all(deletions);
}

/**
 * Get a fresh download URL for an attachment
 * Useful if the URL has expired
 */
export async function refreshDownloadUrl(storagePath: string): Promise<string> {
  const storageRef = ref(storage, storagePath);
  return getDownloadURL(storageRef);
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
}

/**
 * Get a human-readable file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Check if a file is an image
 */
export function isImageFile(mimeType: string): boolean {
  return (DM_ATTACHMENT_LIMITS.allowedImageTypes as readonly string[]).includes(mimeType);
}

/**
 * Get file icon name based on extension
 */
export function getFileIconName(filename: string): string {
  const ext = getFileExtension(filename);

  switch (ext) {
    case 'pdf':
      return 'file-text';
    case 'doc':
    case 'docx':
      return 'file-text';
    case 'xls':
    case 'xlsx':
      return 'file-spreadsheet';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return 'image';
    default:
      return 'file';
  }
}

// ============================================================================
// Donation Page Builder Images
// ============================================================================

// Storage path for donation page builder images
const getDonationImageStoragePath = (filename: string) =>
  `artifacts/${appId}/donation/images/${Date.now()}_${filename}`;

// Donation image limits
export const DONATION_IMAGE_LIMITS = {
  maxFileSize: 10 * 1024 * 1024, // 10 MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const,
};

/**
 * Validate an image file for donation page builder upload
 */
export function validateDonationImage(file: File): { valid: boolean; error?: string } {
  if (file.size > DONATION_IMAGE_LIMITS.maxFileSize) {
    return {
      valid: false,
      error: `Image size exceeds ${DONATION_IMAGE_LIMITS.maxFileSize / (1024 * 1024)}MB limit`,
    };
  }

  if (!(DONATION_IMAGE_LIMITS.allowedTypes as readonly string[]).includes(file.type)) {
    return {
      valid: false,
      error: 'Only JPG, PNG, GIF, and WebP images are allowed',
    };
  }

  return { valid: true };
}

/**
 * Upload an image to Firebase Storage for donation page builder
 */
export async function uploadDonationImage(
  file: File
): Promise<{ url: string; storagePath: string }> {
  // Validate file
  const validation = validateDonationImage(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Create storage reference
  const storagePath = getDonationImageStoragePath(file.name);
  const storageRef = ref(storage, storagePath);

  // Set metadata
  const metadata: UploadMetadata = {
    contentType: file.type,
    customMetadata: {
      originalName: file.name,
      context: 'donation-page-builder',
    },
  };

  // Upload file
  const snapshot = await uploadBytes(storageRef, file, metadata);

  // Get download URL
  const url = await getDownloadURL(snapshot.ref);

  return { url, storagePath };
}

/**
 * Delete a donation image from Firebase Storage
 */
export async function deleteDonationImage(storagePath: string): Promise<void> {
  const storageRef = ref(storage, storagePath);
  await deleteObject(storageRef);
}

// ============================================================================
// Community Chat Attachments (Images Only)
// ============================================================================

/**
 * Validate an image file for community chat upload (PNG/JPG only)
 */
export function validateCommunityImage(file: File): { valid: boolean; error?: string } {
  if (file.size > COMMUNITY_IMAGE_LIMITS.maxFileSize) {
    return {
      valid: false,
      error: `Image size exceeds ${COMMUNITY_IMAGE_LIMITS.maxFileSize / (1024 * 1024)}MB limit`,
    };
  }

  if (!(COMMUNITY_IMAGE_LIMITS.allowedTypes as readonly string[]).includes(file.type)) {
    return {
      valid: false,
      error: 'Only PNG and JPG images are allowed in community chat',
    };
  }

  return { valid: true };
}

/**
 * Upload an image to Firebase Storage for community chat
 */
export async function uploadCommunityAttachment(file: File): Promise<Attachment> {
  // Validate file
  const validation = validateCommunityImage(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Create storage reference
  const storagePath = getCommunityAttachmentStoragePath(file.name);
  const storageRef = ref(storage, storagePath);

  // Set metadata
  const metadata: UploadMetadata = {
    contentType: file.type,
    customMetadata: {
      originalName: file.name,
      context: 'community-chat',
    },
  };

  // Upload file
  const snapshot = await uploadBytes(storageRef, file, metadata);

  // Get download URL
  const url = await getDownloadURL(snapshot.ref);

  // Return attachment object
  return {
    type: 'image',
    url,
    name: file.name,
    size: file.size,
    mimeType: file.type,
    storagePath,
  };
}
